/**
 * Drafting the grounds of the department's own writ petition.
 *
 * **The other posture, and the screen says so.** D18 to D20 answer somebody
 * else's paragraphs. This attacks an order — and what it produces is `[D]`: our
 * unsourced draft, no AWEIL sample and no prescribed form behind it. Stop list 8
 * governs the rest: the flag is on the blocks, it is on this screen, and it
 * comes off when the office has settled the form, not when the page looks tidy.
 *
 * What sits above the grounds is what the document cannot report about itself:
 * what the Tribunal was read to have held. That is the thing to check first,
 * because a ground against a holding the order does not contain is the failure
 * this document has that a reply does not — a reply's paragraph is printed on
 * the page, and a holding is not.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '../api/client'
import type { GroundView, HoldingView } from '../api/client'
import { ANSWERS, paperLabel } from './pleading'

/** The petitions this office drafts grounds for. One, and named. */
export const PETITIONS = new Set(['D21'])

export const isPetition = (documentType: string | undefined): boolean =>
  Boolean(documentType && PETITIONS.has(documentType.toUpperCase()))

export function GroundsPanel({
  caseId,
  documentType,
  instruction,
  onInstruction,
  chosen,
  onChoose,
  onDraft,
  pending,
}: {
  caseId: string
  documentType: string
  instruction: string
  onInstruction: (value: string) => void
  chosen: string | null
  onChoose: (id: string | null) => void
  onDraft: () => void
  pending: boolean
}) {
  const attacks = ANSWERS[documentType.toUpperCase()] ?? "the Tribunal's order"
  const papers = useQuery({
    queryKey: ['pleading-papers', caseId, documentType],
    queryFn: () => api.pleadingSourcePapers(caseId, documentType),
    retry: false,
  })
  const rows = papers.data ?? []

  return (
    <div className="replies-draft" data-testid="grounds-draft">
      <p className="muted">
        This drafts the grounds of the department&apos;s own petition under Article 226 against{' '}
        {attacks} — one ground for each thing the Tribunal held. It is read as it was passed,
        so each ground names the paragraph of the order it attacks.
      </p>

      <p className="small warn" data-testid="grounds-unsourced">
        <strong>These are our drafted grounds, and nothing stands behind them.</strong> There is
        no AWEIL sample and no prescribed form for this document, so every ground is marked
        unverified on the page and in the file. Counsel settles them before anything is filed.
      </p>

      {papers.isLoading ? (
        <p className="small muted">Reading the papers on this file…</p>
      ) : rows.length === 0 ? (
        <p className="small muted" data-testid="grounds-no-papers">
          No papers are on this file yet. Upload {attacks} through <strong>Papers</strong> on the
          case first — it belongs on the file whatever is drafted from it.
        </p>
      ) : (
        <label className="field">
          <span className="label">Which paper is {attacks}?</span>
          <select
            aria-label={`The paper that is ${attacks}`}
            value={chosen ?? ''}
            onChange={(e) => onChoose(e.target.value || null)}
            disabled={pending}
            data-testid="grounds-paper"
          >
            <option value="">Choose a paper…</option>
            {rows.map((paper) => (
              <option key={paper.id} value={paper.id}>
                {paperLabel(paper)}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="field">
        <span className="label">
          What is wrong with the order?{' '}
          <span className="small muted">Optional, in your own words.</span>
        </span>
        <textarea
          rows={3}
          value={instruction}
          onChange={(e) => onInstruction(e.target.value)}
          disabled={pending}
          placeholder="The Tribunal did not deal with the benchmark of Very Good in four of five APARs."
          data-testid="grounds-instruction"
        />
      </label>

      <button
        type="button"
        className="btn"
        onClick={onDraft}
        disabled={pending || !chosen}
        data-testid="grounds-generate"
      >
        {pending ? 'Reading the order…' : 'Draft the grounds'}
      </button>
      {pending ? (
        <p className="small muted" style={{ marginTop: 8 }}>
          The order is read once, then its holdings are attacked a few at a time, all at once.
          About a minute.
        </p>
      ) : null}
    </div>
  )
}

export function Grounds({
  lettered,
  drafted,
  grounds,
  holdings,
  unattacked,
  failures,
}: {
  lettered: string
  drafted: number
  grounds: GroundView[]
  holdings: HoldingView[]
  unattacked: string[]
  failures: string[]
}) {
  const [copiedNow, setCopiedNow] = useState(false)
  const [showing, setShowing] = useState(false)

  const copy = () => {
    void navigator.clipboard.writeText(lettered).then(() => {
      setCopiedNow(true)
      window.setTimeout(() => setCopiedNow(false), 2000)
    })
  }

  const complete = holdings.length > 0 && unattacked.length === 0 && failures.length === 0

  return (
    <div className={complete ? 'replies settled' : 'replies'} data-testid="grounds">
      <div className="row between">
        <p className="strong">
          {holdings.length
            ? `${drafted} grounds against ${holdings.length} holdings.`
            : `${drafted} grounds.`}
        </p>
        <button type="button" className="quiet" onClick={copy} data-testid="grounds-copy">
          {copiedNow ? 'Copied' : 'Copy the grounds'}
        </button>
      </div>

      {/* **Read this before the grounds.** A reply answers a paragraph printed
          on the page; a ground attacks a holding that had to be read out of the
          order. If a holding here is not one the Tribunal made, the ground
          against it is an attack on nothing, and no amount of reading the ground
          itself would show that. */}
      {/* **What is weak about each ground, beside the ground.** The assessment
          is written for the person deciding whether to press it and it says
          when not to — it belongs on the screen and never in the filed
          petition, which carries the ground alone. */}
      {grounds.length ? (
        <ol className="assessments" data-testid="grounds-assessments">
          {grounds.map((g, n) => (
            <li key={`${g.paragraph}-${n}`}>
              <span className={`chip ${g.strength === 'strongest' ? 'good' : 'neutral'}`}>
                {g.strength}
              </span>{' '}
              <span className="mono">¶{g.paragraph}</span>
              {g.assessment ? <p className="small muted">{g.assessment}</p> : null}
            </li>
          ))}
        </ol>
      ) : null}

      {holdings.length ? (
        <p className="small">
          <button
            type="button"
            className="quiet"
            onClick={() => setShowing(!showing)}
            data-testid="grounds-holdings-toggle"
          >
            {showing ? 'Hide' : 'Check'} what the order was read to have held
          </button>
        </p>
      ) : null}

      {showing ? (
        <ul className="holdings" data-testid="grounds-holdings">
          {holdings.map((h) => (
            <li key={h.paragraph}>
              <span className="mono">¶{h.paragraph}</span> <span>{h.held}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {unattacked.length ? (
        <p className="small warn" data-testid="grounds-unattacked">
          <strong>Nothing attacks {unattacked.join(', ')}.</strong> A holding left standing
          survives the petition whatever else succeeds. Draft again to cover{' '}
          {unattacked.length === 1 ? 'it' : 'them'}.
        </p>
      ) : null}

      {failures.map((f, n) => (
        <p className="small warn" key={n}>
          A batch did not come back — {f}
        </p>
      ))}

    </div>
  )
}
